import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Zoom
} from '@mui/material';
import {
  Close,
  ZoomIn,
  ZoomOut,
  Download,
  Print
} from '@mui/icons-material';

const ImageViewerModal = ({ open, onClose, imageUrl, title = "Certificate/Document" }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Utility function to extract filename from docLink URL
  const extractFileNameFromUrl = (url) => {
    if (!url) return 'Document';
    
    try {
      // Extract the filename after the last slash
      const urlPath = url.split('/').pop();
      
      // If the URL contains an underscore, extract everything after the first underscore
      if (urlPath && urlPath.includes('_')) {
        const parts = urlPath.split('_');
        if (parts.length > 1) {
          // Join all parts after the first underscore (in case filename has underscores)
          return parts.slice(1).join('_');
        }
      }
      
      // If no underscore pattern, return the filename as is
      return urlPath || 'Document';
    } catch (error) {
      console.error('Error extracting filename:', error);
      return 'Document';
    }
  };

  // Get display title - use extracted filename if title is generic or just a number
  const getDisplayTitle = () => {
    if (!title || title === 'Certificate/Document' || title.match(/^\d+$/)) {
      return extractFileNameFromUrl(imageUrl);
    }
    return title;
  };

  const handleImageLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setError(true);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleDownload = () => {
    if (imageUrl) {
      const filename = extractFileNameFromUrl(imageUrl);
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (imageUrl) {
      const filename = getDisplayTitle();
      
      // Create a hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      
      // Write the print content to the iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.write(`
        <html>
          <head>
            <title>${filename}</title>
            <style>
              @page { margin: 0; }
              body { 
                margin: 0; 
                padding: 20px;
                display: flex; 
                justify-content: center; 
                align-items: center; 
                min-height: 100vh;
                font-family: Arial, sans-serif;
              }
              img { 
                max-width: 100%; 
                max-height: 90vh; 
                object-fit: contain;
                border: 1px solid #ddd;
              }
              .header {
                position: absolute;
                top: 10px;
                left: 20px;
                font-size: 12px;
                color: #666;
              }
            </style>
          </head>
          <body>
            <div class="header">${filename}</div>
            <img src="${imageUrl}" alt="${filename}" />
          </body>
        </html>
      `);
      iframeDoc.close();
      
      // Wait for image to load, then print
      const img = iframeDoc.querySelector('img');
      img.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      
      // Fallback if image fails to load
      img.onerror = () => {
        alert('Unable to print image. Please try downloading instead.');
        document.body.removeChild(iframe);
      };
    }
  };

  const resetZoom = () => {
    setZoom(1);
  };

  const handleClose = () => {
    setZoom(1);
    setLoading(true);
    setError(false);
    onClose();
  };

  const isImage = imageUrl && (
    imageUrl.toLowerCase().includes('.jpg') ||
    imageUrl.toLowerCase().includes('.jpeg') ||
    imageUrl.toLowerCase().includes('.png') ||
    imageUrl.toLowerCase().includes('.gif') ||
    imageUrl.toLowerCase().includes('.bmp') ||
    imageUrl.toLowerCase().includes('.webp')
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh',
          zIndex: 10001, // Higher than sidebar's 10000
          margin: { xs: 1, sm: 2 }, // Add margin for mobile
          width: { xs: 'calc(100% - 16px)', sm: 'auto' }, // Adjust width for mobile
        }
      }}
      sx={{
        zIndex: 10001, // Ensure dialog is above sidebar
        '& .MuiBackdrop-root': {
          zIndex: 10000, // Backdrop should cover sidebar but be below paper
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {getDisplayTitle()}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {!imageUrl ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No document available
            </Typography>
          </Box>
        ) : !isImage ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Document Preview Not Available
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              This document cannot be previewed in the browser.
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleDownload}
              startIcon={<Download />}
              sx={{ mt: 2 }}
            >
              Download Document
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: { xs: '50vh', sm: '60vh' }, // Smaller height on mobile
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'auto',
              position: 'relative',
              backgroundColor: '#f5f5f5'
            }}
          >
            {loading && (
              <CircularProgress />
            )}
            {error ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography color="error" gutterBottom>
                  Failed to load image
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={handleDownload}
                  startIcon={<Download />}
                >
                  Download Instead
                </Button>
              </Box>
            ) : (
              <Box
                component="img"
                src={imageUrl}
                alt={getDisplayTitle()}
                onLoad={handleImageLoad}
                onError={handleImageError}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transform: `scale(${zoom})`,
                  transition: 'transform 0.3s ease',
                  cursor: zoom > 1 ? 'grab' : 'default',
                  '&:active': {
                    cursor: zoom > 1 ? 'grabbing' : 'default'
                  }
                }}
              />
            )}
          </Box>
        )}
      </DialogContent>

      {imageUrl && isImage && !error && (
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleZoomOut} disabled={zoom <= 0.5}>
              <ZoomOut />
            </IconButton>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={resetZoom}
              sx={{ minWidth: '60px' }}
            >
              {Math.round(zoom * 100)}%
            </Button>
            <IconButton onClick={handleZoomIn} disabled={zoom >= 3}>
              <ZoomIn />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              onClick={handlePrint}
              startIcon={<Print />}
              size="small"
            >
              Print
            </Button>
            
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ImageViewerModal;